import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core';
import CustomModal from '../customModal';
import UserAvatar from '../userAvatar';
import GridTable from '../gridTable';
import StatusIndicator from '../statusIndicator';
import Tooltip from '../tooltip';
import ActionButton from '../actionButton';
import AlertDialog from '../alertDialog';
import { showSuccessMessage } from '../../../utility/uiUtils';
import {
  prepareUserAbsenceRequestsTableHeaders,
  prepareUserAbsenceRequestsTableData,
  prepareUserAbsenceRequestsTableActionsHeader,
} from '../../pages/peopleAbsencesPage/config';
import { prepareTranslatedAbsenceRequestStatuses } from '../../../utility/absence';
import { isArray, isArrayEmpty } from '../../../utility/helpers';
import { ACTION_BUTTON_TYPES } from '../actionButton/config';
import { ABSENCE_REQUEST_STATUS_TYPES } from '../../../constants/statuses';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      width: 964,
    },
    footer: {
      borderTop: 'none',
      justifyContent: 'center',
    },
    userLabel: {
      fontFamily: 'ProximaNova-Bold',
      fontSize: 16,
      lineHeight: '20px',
    },
    user: {
      marginBottom: spacing(6),
    },
    tableHeader: {
      borderRadius: 4,
    },
    tableHeaderCell: {
      color: primary.indigo2,
      padding: spacing(1.5, 2),
      [breakpoints.up('xLg')]: {
        padding: spacing(1.5, 2),
      },
    },
    tableRowCell: {
      padding: spacing(4, 2),
      [breakpoints.up('xLg')]: {
        padding: spacing(4, 2),
      },
    },
    actionsCell: {
      overflow: 'visible',
      paddingLeft: 0,
      paddingTop: spacing(0),
      paddingBottom: spacing(0),
      paddingRight: spacing(2),

      [breakpoints.up('xLg')]: {
        paddingRight: spacing(2),
      },
    },
    icon: {
      cursor: 'default',
    },
    actionsWrapper: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
    },
    statusActions: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 32px)',
      gridColumnGap: 4,
    },
  })
);

const AbsenceRequestDialog = ({
  translations,
  auth,
  isOpened,
  user = null,
  absenceRequests = [],
  isDoubleApprove,
  onApproveRequest,
  onRejectRequest,
  onClose,
}) => {
  const classes = useStyles();

  const isCurrentUser = user?.id === auth.id;

  const [isApproveRequestOpened, setIsApproveRequestOpened] = useState(false);
  const [isRejectRequestOpened, setIsRejectRequestOpened] = useState(false);
  const [absenceRequest, setAbsenceRequest] = useState(null);

  const requestStatuses = prepareTranslatedAbsenceRequestStatuses(
    translations.requestStatuses
  );

  const abenceRequestsTableHeaders = prepareUserAbsenceRequestsTableHeaders(
    classes,
    translations.table
  );

  const absenceRequestsTableActions =
    prepareUserAbsenceRequestsTableActionsHeader(classes, translations.table);

  const handleOpenApproveRequest = currentRequest => () => {
    setIsApproveRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseApproveRequest = () => {
    setIsApproveRequestOpened(false);
    setAbsenceRequest(null);
  };

  const handleApproveRequest = async () => {
    try {
      await onApproveRequest(absenceRequest);
      showSuccessMessage(translations.approveRequestDialog.approveSuccess);
    } finally {
      handleCloseApproveRequest();
    }
  };

  const handleOpenRejectRequest = currentRequest => () => {
    setIsRejectRequestOpened(true);
    setAbsenceRequest(currentRequest);
  };

  const handleCloseRejectRequest = () => {
    setIsRejectRequestOpened(false);
    setAbsenceRequest(null);
  };

  const handleRejectRequest = async () => {
    try {
      await onRejectRequest(absenceRequest);
      showSuccessMessage(translations.rejectRequestDialog.rejectSuccess);
    } finally {
      handleCloseRejectRequest();
    }
  };

  useEffect(() => {
    if (
      isArrayEmpty(absenceRequests) ||
      absenceRequests.every(
        request => request.status === ABSENCE_REQUEST_STATUS_TYPES.APPROVED
      )
    ) {
      onClose();
      setAbsenceRequest(null);
    }
  }, [absenceRequests, onClose]);

  const renderStatus = currentStatus => (
    <StatusIndicator status={currentStatus} isDoubleApprove={isDoubleApprove} />
  );

  const renderAbsence = currentAbsence => (
    <Tooltip
      customIconClass={classes.icon}
      text={currentAbsence.name}
      icon={currentAbsence.absenceIcon}
    />
  );

  const renderRequestTableActions = (request, hasStatusActions) => {
    return (
      <div className={classes.actionsWrapper}>
        {hasStatusActions && (
          <div className={classes.statusActions}>
            <ActionButton
              type={ACTION_BUTTON_TYPES.CLOSE}
              tooltipText={translations.menuItems.reject}
              onClickHandler={handleOpenRejectRequest(request)}
              disabledTooltipText={
                translations.rejectRequestAbsenceButtonDisabled
              }
              isSquared
            />
            <ActionButton
              type={ACTION_BUTTON_TYPES.ACCEPT}
              tooltipText={translations.menuItems.approve}
              onClickHandler={handleOpenApproveRequest(request)}
              isSquared
            />
          </div>
        )}
      </div>
    );
  };

  const absenceRequestsTableData = prepareUserAbsenceRequestsTableData(
    translations.table,
    auth,
    absenceRequests || [],
    requestStatuses,
    false,
    true,
    isCurrentUser,
    isDoubleApprove,
    renderAbsence,
    renderStatus,
    renderRequestTableActions
  );

  return (
    <CustomModal
      paperClass={classes.root}
      buttonsWrapperClass={classes.footer}
      title={translations.title}
      closeButtonLabel={translations.cancel}
      isOpened={isOpened}
      onClose={onClose}
    >
      <div>
        {user && (
          <UserAvatar
            className={classes.user}
            labelClass={classes.userLabel}
            user={user}
            caption
          />
        )}
        {isArray(absenceRequests) && !isArrayEmpty(absenceRequests) && (
          <GridTable
            translations={translations.table}
            customHeaderClass={classes.tableHeader}
            headers={abenceRequestsTableHeaders}
            headerActions={absenceRequestsTableActions}
            rows={absenceRequestsTableData}
          />
        )}
        <AlertDialog
          translations={translations.approveRequestDialog}
          isOpened={isApproveRequestOpened}
          onClose={handleCloseApproveRequest}
          onConfirm={handleApproveRequest}
          isWarning
        />
        <AlertDialog
          translations={translations.rejectRequestDialog}
          isOpened={isRejectRequestOpened}
          onClose={handleCloseRejectRequest}
          onConfirm={handleRejectRequest}
          isWarning
        />
      </div>
    </CustomModal>
  );
};

AbsenceRequestDialog.propTypes = {
  translations: PropTypes.object.isRequired,
  isOpened: PropTypes.bool.isRequired,
  user: PropTypes.object,
  absenceRequests: PropTypes.arrayOf(PropTypes.object),
  isDoubleApprove: PropTypes.bool.isRequired,
  onApproveRequest: PropTypes.func.isRequired,
  onRejectRequest: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AbsenceRequestDialog;
